import axios from 'axios';
import laroute from '@laroute';
import LoggingService from '@shared/js/LoggingService';

/**
 * HTTP Client for API calls
 * Provides a clean, consistent interface for all API operations
 */
class HttpClient {
	constructor(baseConfig = {}) {
		this.client = axios.create({
			timeout: 30000,
			headers: {
				'Content-Type':     'application/json',
				'X-Requested-With': 'XMLHttpRequest',
			},
			...baseConfig,
		});

		// Track ongoing requests for auto-abort functionality
		this.pendingRequests = new Map();

		// Request interceptor for authentication and abort signal handling
		this.client.interceptors.request.use(
			(config) => {
				const method = (config.method || 'get').toLowerCase();
				const shouldTrack = method === 'get';

				if (shouldTrack) {
					// Handle auto-abort for duplicate requests
					const requestKey = this.getRequestKey(config);
					const existingRequest = this.pendingRequests.get(requestKey);

					// Auto-abort previous request unless explicitly disabled
					if (existingRequest && config.autoAbort !== false) {
						existingRequest.abortController?.abort();
						this.pendingRequests.delete(requestKey);
					}

					// Create new AbortController for this request
					const abortController = new AbortController();
					config.signal = abortController.signal;

					// Store the request
					this.pendingRequests.set(requestKey, {
						abortController,
						url:    config.url,
						method: config.method,
					});

					// Clean up when request completes
					const cleanup = () => {
						this.pendingRequests.delete(requestKey);
					};

					// Store cleanup function for later use
					config._cleanup = cleanup;
				}

				return config;
			},
			(error) => Promise.reject(error)
		);

		// Response interceptor for error handling and cleanup
		this.client.interceptors.response.use(
			(response) => {
				// Clean up completed request
				if (response.config._cleanup) {
					response.config._cleanup();
				}
				return response;
			},
			(error) => {
				// Clean up failed request
				if (error.config?._cleanup) {
					error.config._cleanup();
				}

				// Don't treat aborted requests as errors
				if (axios.isCancel?.(error) || error.code === 'ERR_CANCELED' || error.name === 'CanceledError') {
					return Promise.reject(error);
				}

				// Check if custom logging is enabled for this request
				if (error.config?.logging?.enabled) {
					const logConfig = error.config.logging;
					const level = logConfig.level || 'error';
					const message = logConfig.message || 'API Error';
					const scope = logConfig.scope || 'http-client';

					const context = {
						url:          error.config?.url,
						status:       error.response?.status,
						method:       error.config?.method,
						responseData: error.response?.data,
						...logConfig.context,
					};

					LoggingService.logToSentry(level, message, {
						scope,
						context,
					});
				} else {
					// Global error handling for requests without custom logging - use console only
					if (error.response?.status === 401) {
						// Handle unauthorized
						console.warn('Unauthorized request:', {
							url:    error.config?.url,
							status: error.response?.status,
							method: error.config?.method,
						});
					} else if (error.response?.status >= 500) {
						// Handle server errors
						console.error('Server error:', {
							url:          error.config?.url,
							status:       error.response?.status,
							method:       error.config?.method,
							responseData: error.response?.data,
							...error,
						});
					}
				}

				return Promise.reject(error);
			}
		);
	}

	// Generate a unique key for request deduplication
	getRequestKey(config) {
		const baseURL = config.baseURL || this.client.defaults.baseURL || window.location.origin;
		const method = config.method?.toUpperCase() || 'GET';
		const url = new URL(config.url, baseURL);

		return `${method}_${url.origin}${url.pathname}`;
	}

	// Abort all pending requests
	abortAll() {
		for (const [, request] of this.pendingRequests) {
			request.abortController?.abort();
		}
		this.pendingRequests.clear();
	}

	// Abort specific request by URL
	abortRequest(method, url, { baseURL } = {}) {
		const key = this.getRequestKey({
			method,
			url,
			baseURL: baseURL || this.client.defaults.baseURL || window.location.origin,
		});
		const request = this.pendingRequests.get(key);
		if (request) {
			request.abortController?.abort();
			this.pendingRequests.delete(key);
		}
	}

	// Generic request methods
	async get(url, config = {}) {
		return this.client.get(url, { autoAbort: true, ...config });
	}

	async post(url, data = {}, config = {}) {
		return this.client.post(url, data, { ...config });
	}

	async put(url, data = {}, config = {}) {
		return this.client.put(url, data, { ...config });
	}

	async patch(url, data = {}, config = {}) {
		return this.client.patch(url, data, { ...config });
	}

	async delete(url, config = {}) {
		return this.client.delete(url, { ...config });
	}

	// Methods to disable auto-abort for concurrent requests to the same endpoint
	async getConcurrent(url, config = {}) {
		return this.client.get(url, { autoAbort: false, ...config });
	}

	async postConcurrent(url, data = {}, config = {}) {
		return this.client.post(url, data, { autoAbort: false, ...config });
	}

	// User API methods
	async getUserProfile(params = {}) {
		const queryString = new URLSearchParams(params).toString();
		const url = `${laroute.route('api.user.profile')}${queryString ? '?' + queryString : ''}`;
		return this.get(url, { withCredentials: true });
	}

	async updateUserProfile(profileData, locale = null) {
		const url = locale ? `${laroute.route('api.user.profile')}?locale=${locale}` : laroute.route('api.user.profile');
		return this.patch(url, profileData, { withCredentials: true });
	}

	async setUserLocale(locale) {
		return this.patch(laroute.route('api.user.profile'), { locale }, { withCredentials: true });
	}

	// Arena Authentication methods
	async logout(profile = null) {
		const requests = [];

		// Logout from external provider if available
		if (profile?.externalLogoutUrl) {
			requests.push(
				axios.post(profile.externalLogoutUrl, {}, {
					withCredentials: true,
					timeout:         5000,
				}).catch(() => null) // Ignore external logout failures
			);
		}

		// Logout from arena
		requests.push(
			this.post(laroute.route('arena.api.arena_login.logout'), {}, { withCredentials: true })
		);

		return Promise.all(requests);
	}

	async createQrToken(body = {}) {
		return this.post(laroute.route('arena.api.arena_login.create_qr_token'), body);
	}

	// Following/Follows API methods
	async getFollows() {
		return this.get(laroute.route('arena.api.follows.get'));
	}

	async getFollowables() {
		return this.get(laroute.route('arena.api.follows.related'));
	}

	async follow(contextId, contextableId) {
		return this.put(laroute.route('arena.api.follows.add', { contextId, contextableId }));
	}

	async unfollow(contextId, contextableId) {
		return this.delete(laroute.route('arena.api.follows.delete', { contextId, contextableId }));
	}

	// Bookmark API methods
	async addBookmark(type, id) {
		return this.put(laroute.route('arena.api.bookmark.add', { type, id }));
	}

	async removeBookmark(type, id) {
		return this.delete(laroute.route('arena.api.bookmark.delete', { type, id }));
	}

	// Events API methods
	async getEventsCached(params = {}) {
		const queryString = new URLSearchParams(params).toString();
		return this.get(`${laroute.route('api.events.cached')}?${queryString}`);
	}

	async getDynamicAlbumEvents(options = {}) {
		return this.get(laroute.route('api.events.dynamic_album', options));
	}

	async getFeed(larouteParams = {}, urlSearchParams = {}) {
		const queryString = urlSearchParams.toString();
		return this.get(`${laroute.route('api.feed', larouteParams)}?${queryString}`);
	}

	async getEventPlayout(eventId) {
		const loggingConfig = {
			enabled: true,
			level:   'fatal',
			message: 'Event Playout Error',
			scope:   'event-playout',
			context: {
				eventId,
			},
		};
		return this.get(laroute.route('api.event.playout', { event: eventId }), { logging: loggingConfig });
	}

	async getEvent(eventId) {
		return this.get(laroute.route('api.event', { event: eventId }));
	}

	async getEventVideoParts(eventId, params = {}) {
		return this.get(laroute.route('api.event.video_parts', { event: eventId, ...params }));
	}

	// Payment API methods
	async getPaymentOffers(larouteParams = {}) {
		return this.get(laroute.route('api.payment.offers', larouteParams));
	}

	async getPaymentVoucher(code, offerId) {
		return this.get(laroute.route('api.payment.voucher', { code, offerId }));
	}

	async cancelSubscription(paymentOrder) {
		return this.put(laroute.route('arena.api.payment.subscription.cancel', { paymentOrder }), {}, { withCredentials: true });
	}

	// Geoblocking API methods
	async checkArenaGeoblocking() {
		return this.get(laroute.route('api.arena_geoblocking'));
	}

	// Survey API methods
	async submitSurvey(surveyData) {
		return this.post(laroute.route('api.survey'), surveyData);
	}

	// League API methods
	async getLeagueTableSeason(leagueId, season) {
		return this.get(laroute.route('api.league.table_season', { league: leagueId, season }));
	}

	async getLeagueTopScorersSeason(leagueId, season) {
		return this.get(laroute.route('api.league.top_scorers_season', { league: leagueId, season }));
	}

	// Contact API methods
	async submitContactForm(contactData) {
		const loggingConfig = {
			enabled: true,
			level:   'fatal',
			message: 'Contact Form Submission Error',
			scope:   'contact-form',
			context: {
				contactData,
			},
		};
		return this.post(laroute.route('api.contact'), contactData, { logging: loggingConfig });
	}

	// Event video crop API methods
	async getEventVideoCrops(params = {}) {
		const queryString = new URLSearchParams(params).toString();
		return this.get(`${laroute.route('api.events.video_crops')}?${queryString}`);
	}

	async deleteEventVideoCrop(eventVideoCropId) {
		return this.delete(laroute.route('api.event_video_crop.delete', { eventVideoCrop: eventVideoCropId }));
	}

	async getEventVideoCropPlayout(eventVideoCropId) {
		return this.get(laroute.route('api.event_video_crop.playout', { eventVideoCrop: eventVideoCropId }));
	}

	async getMomentsLanePlayouts(eventVideoCropIds) {
		return this.post(laroute.route('api.moments_lane.playouts'), { eventVideoCropIds });
	}

	async createVideoCrop(eventId, data, config = {}) {
		const loggingConfig = {
			enabled: true,
			level:   'fatal',
			message: 'MultiFormatTool: Cropping Backend Error',
			scope:   'multi-format-tool',
			context: {
				eventId,
			},
		};

		return this.post(laroute.route('api.event.video_crop.create', { event: eventId }), data, {
			...config,
			logging:         loggingConfig,
			withCredentials: true,
		});
	}

	async createCommunityVideoCrop(eventId, data, config = {}) {
		const loggingConfig = {
			enabled: true,
			level:   'fatal',
			message: 'MultiFormatTool: Cropping Backend Error',
			scope:   'multi-format-tool',
			context: {
				eventId,
			},
		};

		return this.post(laroute.route('api.event.community_video_crop.create', { event: eventId }), data, {
			...config,
			logging: loggingConfig,
		});
	}

	async getShortlink(eventVideoCropId) {
		return this.post(laroute.route('api.event_video_crop.shortlink', { eventVideoCrop: eventVideoCropId }));
	}

	async createVideoCropNotification(eventVideoCropId, email) {
		return this.post(laroute.route('api.event_video_crop.notify', { eventVideoCrop: eventVideoCropId }), { email });
	}

	// External API methods (for services outside the main API)
	async fetchExternal(url, options = {}) {
		return axios.get(url, {
			timeout: 10000,
			...options,
		});
	}
}

// Create and export the default instance
export const httpClient = new HttpClient();

// Export the class for custom instances if needed
export default HttpClient;
